import React, { useState, useEffect } from 'react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import transactionService from '../services/transactionService';
import accountService from '../services/accountService';
import categoryService from '../services/categoryService';
import { getIconComponent } from '../utils/iconHelper';
import MonthPicker from '../components/MonthPicker';
import {
  MdTrendingUp,
  MdTrendingDown,
  MdAccountBalance,
  MdAttachMoney,
  MdCategory,
  MdCalendarToday,
  MdToday
} from 'react-icons/md';
import './Dashboard.css';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Garantir que sempre pegue a data atual local
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [summary, setSummary] = useState({
    currentBalance: 0,
    monthIncome: 0,
    monthExpense: 0,
    monthBalance: 0,
    pendingIncome: 0,
    pendingExpenses: 0,
  });
  const [accounts, setAccounts] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [topExpenseCategories, setTopExpenseCategories] = useState([]);
  const [topIncomeCategories, setTopIncomeCategories] = useState([]);
  const [categories, setCategories] = useState([]);


  useEffect(() => {
    loadDashboardData();
  }, [selectedMonth]);

  const goToCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    setSelectedMonth(`${year}-${month}-${day}`);
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Criar data de forma mais robusta, evitando problemas de timezone
      // Extrair ano, mês e dia da string yyyy-MM-dd
      const [yearStr, monthStr, dayStr] = selectedMonth.split('-');
      const selectedDate = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr));
      
      const currentYear = selectedDate.getFullYear();
      const currentMonth = selectedDate.getMonth() + 1;
      
      const monthStart = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(selectedDate), 'yyyy-MM-dd');

      // Carregar dados em paralelo
      const [
        summaryResult,
        accountsResult,
        categoriesResult,
        transactionsResult,
        topCategoriesResult
      ] = await Promise.allSettled([
        transactionService.getSummary({ start_date: monthStart, end_date: monthEnd }),
        accountService.getAccounts(),
        categoryService.getCategories(),
        transactionService.getTransactions({ 
          start_date: format(subMonths(selectedDate, 5), 'yyyy-MM-dd'),
          end_date: monthEnd 
        }),
        transactionService.getTopCategories(currentYear, currentMonth, 5)
      ]);

      // Processar resumo
      const summaryData = summaryResult.status === 'fulfilled' ? summaryResult.value.data : {};
      setSummary({
        monthIncome: summaryData.income || summaryData.paid_income || 0,
        monthExpense: summaryData.expense || summaryData.paid_expenses || 0,
        monthBalance: (summaryData.income || summaryData.paid_income || 0) - 
                     (summaryData.expense || summaryData.paid_expenses || 0),
        pendingIncome: summaryData.pending_income || 0,
        pendingExpenses: summaryData.pending_expenses || 0,
      });

      // Processar contas
      const accountsData = accountsResult.status === 'fulfilled' 
        ? (Array.isArray(accountsResult.value.data) ? accountsResult.value.data : [])
        : [];

      // Carregar saldo de cada conta
      const accountsWithBalance = await Promise.all(
        accountsData.map(async (account) => {
          try {
            const balanceData = await accountService.getAccountBalance(account.id);
            return {
              ...account,
              current_balance: balanceData.data?.balance || balanceData.balance || 0,
            };
          } catch (error) {
            return { ...account, current_balance: 0 };
          }
        })
      );

      setAccounts(accountsWithBalance);

      // Calcular saldo total das contas
      const totalBalance = accountsWithBalance.reduce(
        (sum, acc) => sum + (acc.current_balance || 0), 
        0
      );
      setSummary(prev => ({ ...prev, currentBalance: totalBalance }));

      // Processar categorias
      const categoriesData = categoriesResult.status === 'fulfilled' 
        ? (Array.isArray(categoriesResult.value.data) ? categoriesResult.value.data : [])
        : [];
      setCategories(categoriesData);

      // Processar transações para gráficos
      const allTransactions = transactionsResult.status === 'fulfilled' 
        ? (Array.isArray(transactionsResult.value.data) ? transactionsResult.value.data : [])
        : [];

      // Agrupar por mês
      const monthlyMap = {};
      allTransactions.forEach(transaction => {
        if (!transaction.date || !transaction.is_paid) return;
        
        try {
          // Tentar criar data válida de diferentes formatos
          let transDate;
          if (transaction.date.includes('T')) {
            // Já tem hora
            transDate = new Date(transaction.date);
          } else if (transaction.date.includes('-')) {
            // Formato yyyy-MM-dd
            const [y, m, d] = transaction.date.split('-');
            transDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
          } else {
            return; // Data inválida, pular
          }
          
          // Validar se a data é válida
          if (isNaN(transDate.getTime())) {
            console.warn('Data inválida encontrada:', transaction.date);
            return;
          }
          
          const monthKey = format(transDate, 'yyyy-MM');
          if (!monthlyMap[monthKey]) {
            monthlyMap[monthKey] = { month: monthKey, income: 0, expense: 0, balance: 0 };
          }
          const amount = parseFloat(transaction.amount) || 0;
          if (transaction.type === 'income') {
            monthlyMap[monthKey].income += amount;
          } else {
            monthlyMap[monthKey].expense += amount;
          }
          monthlyMap[monthKey].balance = monthlyMap[monthKey].income - monthlyMap[monthKey].expense;
        } catch (error) {
          console.warn('Erro ao processar transação:', transaction.id, error);
        }
      });

      const monthlyData = Object.values(monthlyMap).sort((a, b) => 
        a.month.localeCompare(b.month)
      );
      setMonthlyTrend(monthlyData);

      // Top categorias do endpoint
      let topCategoriesData = { expenses: [], incomes: [] };
      
      if (topCategoriesResult.status === 'fulfilled') {
        const response = topCategoriesResult.value;
        
        // Tratar diferentes estruturas de resposta
        if (response.data && response.data.expenses !== undefined) {
          // Resposta direta: { success, data: { expenses, incomes } }
          topCategoriesData = response.data;
        } else if (response.expenses !== undefined) {
          // Resposta já é os dados: { expenses, incomes }
          topCategoriesData = response;
        } else if (response.data && response.data.data && response.data.data.expenses !== undefined) {
          // Resposta aninhada: { data: { data: { expenses, incomes } } }
          topCategoriesData = response.data.data;
        }
        
        console.log('✅ Top Categorias carregadas:', {
          expenses: topCategoriesData.expenses?.length || 0,
          incomes: topCategoriesData.incomes?.length || 0
        });
      } else if (topCategoriesResult.status === 'rejected') {
        console.error('❌ Erro ao buscar top categorias:', topCategoriesResult.reason);
      }

      const expensesArray = Array.isArray(topCategoriesData.expenses) ? topCategoriesData.expenses : [];
      const incomesArray = Array.isArray(topCategoriesData.incomes) ? topCategoriesData.incomes : [];

      setTopExpenseCategories(expensesArray);
      setTopIncomeCategories(incomesArray);

    } catch (error) {
      console.error('Erro ao carregar dados da dashboard:', error);
      console.error('Stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatCompactCurrency = (value) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}k`;
    }
    return formatCurrency(value);
  };

  const formatMonth = (monthKey) => {
    try {
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return format(date, 'MMM/yy', { locale: ptBR });
    } catch {
      return monthKey;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  const EXPENSE_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#ec4899'];
  const INCOME_COLORS = ['#10b981', '#14b8a6', '#06b6d4', '#22c55e', '#34d399'];

  const healthScore = summary.monthBalance >= 0 ? 'positivo' : 'negativo';
  const healthPercentage = summary.monthIncome > 0 
    ? ((summary.monthBalance / summary.monthIncome) * 100).toFixed(0)
    : 0;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard - Visão Geral</h1>
        <div className="header-controls">
          <div className="month-selector-container">
            <MonthPicker
              value={selectedMonth}
              onChange={setSelectedMonth}
              label="Mês de referência"
            />
          </div>
          <button 
            className="btn-current-month" 
            onClick={goToCurrentMonth}
            title="Voltar ao mês atual"
          >
            <MdToday size={20} />
            Mês Atual
          </button>
        </div>
      </div>

      {/* Cards de Resumo Principal */}
      <div className="summary-cards-grid">
        <div className="summary-card-modern balance-card">
          <div className="card-icon">
            <MdAccountBalance size={32} />
          </div>
          <div className="card-content">
            <h3>Saldo Total</h3>
            <p className="amount">{formatCurrency(summary.currentBalance)}</p>
            <span className="card-subtitle">Soma de todas as contas</span>
          </div>
        </div>

        <div className="summary-card-modern income-card">
          <div className="card-icon">
            <MdTrendingUp size={32} />
          </div>
          <div className="card-content">
            <h3>Receitas do Mês</h3>
            <p className="amount">{formatCurrency(summary.monthIncome)}</p>
            {summary.pendingIncome > 0 && (
              <span className="card-subtitle pending">
                +{formatCurrency(summary.pendingIncome)} pendente
              </span>
            )}
          </div>
        </div>

        <div className="summary-card-modern expense-card">
          <div className="card-icon">
            <MdTrendingDown size={32} />
          </div>
          <div className="card-content">
            <h3>Despesas do Mês</h3>
            <p className="amount">{formatCurrency(summary.monthExpense)}</p>
            {summary.pendingExpenses > 0 && (
              <span className="card-subtitle pending">
                +{formatCurrency(summary.pendingExpenses)} pendente
              </span>
            )}
          </div>
        </div>

        <div className={`summary-card-modern result-card ${healthScore}`}>
          <div className="card-icon">
            <MdAttachMoney size={32} />
          </div>
          <div className="card-content">
            <h3>Resultado do Mês</h3>
            <p className="amount">{formatCurrency(summary.monthBalance)}</p>
            <span className="card-subtitle">
              {healthScore === 'positivo' ? 'Superávit' : 'Déficit'} de {Math.abs(healthPercentage)}%
            </span>
          </div>
        </div>
      </div>

      {/* Contas */}
      {accounts.length > 0 && (
        <div className="dashboard-section">
          <h2>Minhas Contas</h2>
          <div className="accounts-grid">
            {accounts.map(account => (
              <div key={account.id} className="account-card">
                <div className="account-header">
                  <div className="account-icon" style={{ backgroundColor: account.color || '#E3A864' }}>
                    {getIconComponent(account.icon) ? 
                      React.createElement(getIconComponent(account.icon), { size: 24, color: 'white' }) :
                      <MdAccountBalance size={24} color="white" />
                    }
                  </div>
                  <div className="account-info">
                    <h4>{account.name}</h4>
                    <span className="account-type">
                      {account.type === 'checking' && 'Conta Corrente'}
                      {account.type === 'savings' && 'Poupança'}
                      {account.type === 'investment' && 'Investimento'}
                      {account.type === 'cash' && 'Dinheiro'}
                      {account.type === 'credit_card' && 'Cartão de Crédito'}
                      {account.type === 'other' && 'Outra'}
                    </span>
                  </div>
                </div>
                <div className="account-balance">
                  <span className="balance-label">Saldo</span>
                  <span className={`balance-amount ${account.current_balance < 0 ? 'negative' : ''}`}>
                    {formatCurrency(account.current_balance || 0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gráfico de Evolução Mensal */}
      {monthlyTrend.length > 0 && (
        <div className="dashboard-section">
          <h2>Evolução Financeira (Últimos 6 Meses)</h2>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={formatMonth}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tickFormatter={formatCompactCurrency}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => formatMonth(label)}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="income" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Receitas"
                  dot={{ fill: '#10b981', r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="expense" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  name="Despesas"
                  dot={{ fill: '#ef4444', r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="balance" 
                  stroke="#E3A864" 
                  strokeWidth={3}
                  name="Saldo"
                  dot={{ fill: '#E3A864', r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Categorias */}
      <div className="dashboard-section">
        <h2>Principais Categorias do Mês</h2>
        <div className="categories-comparison">
          {/* Top Despesas */}
          <div className="category-list expense-list">
            <h3>
              <MdTrendingDown size={20} />
              Top Despesas
            </h3>
            {topExpenseCategories.length > 0 ? (
              topExpenseCategories.map((cat, index) => {
                const CategoryIcon = getIconComponent(cat.icon);
                return (
                  <div key={cat.category_id || index} className="category-item">
                    <div className="category-rank">{index + 1}</div>
                    <div className="category-icon-small" style={{ backgroundColor: cat.color }}>
                      {CategoryIcon && <CategoryIcon size={16} color="white" />}
                    </div>
                    <div className="category-info-inline">
                      <span className="category-name">{cat.name}</span>
                      <span className="category-amount expense">
                        {formatCurrency(cat.amount)}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="empty-message">Nenhuma despesa no mês selecionado</p>
            )}
          </div>

          {/* Top Receitas */}
          <div className="category-list income-list">
            <h3>
              <MdTrendingUp size={20} />
              Top Receitas
            </h3>
            {topIncomeCategories.length > 0 ? (
              topIncomeCategories.map((cat, index) => {
                const CategoryIcon = getIconComponent(cat.icon);
                return (
                  <div key={cat.category_id || index} className="category-item">
                    <div className="category-rank">{index + 1}</div>
                    <div className="category-icon-small" style={{ backgroundColor: cat.color }}>
                      {CategoryIcon && <CategoryIcon size={16} color="white" />}
                    </div>
                    <div className="category-info-inline">
                      <span className="category-name">{cat.name}</span>
                      <span className="category-amount income">
                        {formatCurrency(cat.amount)}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="empty-message">Nenhuma receita no mês selecionado</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
